import { Injectable } from "@angular/core";
import { environment } from "@env";
import { TranslateService } from "@ngx-translate/core";
import {
  MatSnackBarRef,
  SimpleSnackBar,
  MatSnackBar
} from "@angular/material/snack-bar";

export enum NotificationTypes {
  Information = "information",
  Error = "error",
  Warning = "warning",
  Success = "success"
}

@Injectable({
  providedIn: "root"
})
export class NotifyService {
  readonly delay = environment.messageDelay;

  constructor(
    private translateService: TranslateService,
    private matSnackBar: MatSnackBar
  ) {}

  showSuccessMessageWithTranslationKey(
    messageKey: string = "default-success-message"
  ): void {
    this.translateService.get(messageKey).subscribe((message) => {
      this.showSuccessMessage(message);
    });
  }

  showInformationMessageWithTranslationKey(
    messageKey: string = "default-info-message"
  ): void {
    this.translateService.get(messageKey).subscribe((message) => {
      this.showInformationMessage(message);
    });
  }

  showErrorMessageWithTranslationKey(
    messageKey: string,
    error?: any,
    logMessage: string = ""
  ): void {
    let message = "";
    this.translateService.get(messageKey).subscribe((res) => {
      message = res;
      if (error && error.error && error.error.message) {
        message = error.error.message;
      }
      this.error(message, null, this.delay);
    });
  }

  showWarningMessageWithTranslationKey(messageKey: string): void {
    this.translateService.get(messageKey).subscribe((message) => {
      this.showWarningMessage(message);
    });
  }

  showWarningMessage(message: string = ""): void {
    this.warning(message, null, this.delay);
  }

  showSuccessMessage(message: string = ""): void {
    this.success(message, null, this.delay);
  }

  showInformationMessage(message: string = ""): void {
    this.information(message, null, this.delay);
  }

  showErrorMessage(message: string = ""): void {
    this.error(message, null, this.delay);
  }

  /**
   * Opens an information notification
   * @param message
   * @param action (deprecated in portal context)
   * @param duration (deprecated in portal context)
   * @return MatSnackBarRef or undefined in portal context
   */
  success(
    message: string,
    action?: string,
    duration?: number
  ): MatSnackBarRef<SimpleSnackBar> {
    return this.notify(message, NotificationTypes.Success, action, duration);
  }

  /**
   * Opens an warning notification
   * @param message
   * @param action (deprecated in portal context)
   * @param duration (deprecated in portal context)
   * @return MatSnackBarRef or undefined in portal context
   */
  warning(
    message: string,
    action?: string,
    duration?: number
  ): MatSnackBarRef<SimpleSnackBar> {
    return this.notify(message, NotificationTypes.Warning, action, duration);
  }

  /**
   * Opens an error notification
   * @param message
   * @param action (deprecated in portal context)
   * @param duration (deprecated in portal context)
   * @return MatSnackBarRef or undefined in portal context
   */
  error(
    message: string,
    action?: string,
    duration?: number
  ): MatSnackBarRef<SimpleSnackBar> {
    return this.notify(message, NotificationTypes.Error, action, duration);
  }

  /**
   * Opens an information notification
   * @param message
   * @param action (deprecated in portal context)
   * @param duration (deprecated in portal context)
   * @return MatSnackBarRef or undefined in portal context
   */
  information(
    message: string,
    action?: string,
    duration?: number
  ): MatSnackBarRef<SimpleSnackBar> {
    return this.notify(
      message,
      NotificationTypes.Information,
      action,
      duration
    );
  }

  /**
   * Opens a toast notification
   * Adds notification to sessionStorage
   * Emits NotificationItem to (deprecated) subject
   * @param message Message to display
   * @param notificationType Notification Style (optional, default: NotificationTypes.information)
   * @param action Button title (optional, default: no action)
   * @param duration Duration of the notification (optional, default: 2000ms)
   */
  private notify(
    message: string,
    notificationType: NotificationTypes = NotificationTypes.Information,
    action?: string,
    duration: number = 2000
  ): MatSnackBarRef<SimpleSnackBar> {
    return this.matSnackBar.open(message, action, {
      duration: duration,
      horizontalPosition: "center",
      verticalPosition: "top",
      panelClass: notificationType
    });
  }

  showInformationMessageWithTranslationKeyReplaced(
    messageKey: string = "default-info-message",
    replaceWith: string
  ) {
    this.translateService.get(messageKey).subscribe((message) => {
      this.showInformationMessage(message.replace("{0}", replaceWith));
    });
  }
}
